import React, { useContext, useEffect, useRef, useState } from 'react';
import { parse } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Flyout, FlyoutContent, FlyoutFooter, FlyoutTitle } from 'components/Flyout';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import { CharactersRemaining } from 'components/Form';
import { Button, ButtonGroup, CurrencyInput, Dropdown, Form, Input, Textarea } from 'components/HookForm';
import Message from 'components/Message';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar } from 'components/Snackbar';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { URL_SIMPLE_REGEX, getIsTaxExcludedMenuPrice } from 'constants/logic';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { format12HourTimeLowercase } from 'helpers/intl-fns';
import { getDecagonChatPath } from 'helpers/link-generation';
import segment from 'helpers/segment';
import { selectModals } from 'components/Modal/Modal.selectors';
import { selectActiveVenue, selectUserInfo } from 'selectors/general';
import { selectLocale } from 'selectors/locale-data';
import { selectIsMobile } from 'selectors/ui';

import {
  ADD_ITEM_FLYOUT_ID_PREFIX,
  DEFAULT_AVAILABILITY,
  DEFAULT_DURATION,
  DESCRIPTION_INPUT_HEIGHT_IN_LINES,
  DESCRIPTION_MAX_INPUT_LENGTH,
  NAME_MAX_INPUT_LENGTH,
} from '../Items.constants';
import { createItem, submitMenuPricing } from '../Items.helpers';
import { generateFlyoutId } from '../Items.utils';
import { ITEM_AVAILABILITY_MODAL_NAME } from './ItemAvailabilityModal';
import { ITEM_IN_REVIEW_MODAL_NAME } from './ItemInReviewModal';
import { ItemFormContext } from './ItemFormContext';
import { ITEM_SUBMIT_ERROR_MODAL_NAME } from './ItemSubmitErrorModal';
import { messages } from './ItemForm.messages';

import './ItemForm.scss';

const { block, element } = bem('item-form');
const FORM_NAME = 'add-item-form';
const MODAL_ID = 'item-availability-modal-add';

function updateAvailabilityRecurrence(availabilityRecurrence, updatedAvailability) {
  return availabilityRecurrence.map(day => {
    const updatedAvailabilityDay = updatedAvailability.find(updatedDay => updatedDay.weekday === day.weekday);
    if (updatedAvailabilityDay) {
      return updatedAvailabilityDay;
    }
    return {
      ...day,
      available: false,
    };
  });
}

const errorSnackbarDescriptionWithValues = contactPath => ({
  ...messages.errorAddItemDescription,
  values: {
    'contact-us-link': chunks => (
      <Link className={element('snackbar-link')} to={contactPath}>
        {chunks}
      </Link>
    ),
  },
});

const AddItemForm = ({
  isLoading,
  categoryTags,
  currencyCode,
  existingItemNames,
  foodAndBeverageL1Tag,
  onSuccess,
  defaultAvailability,
}) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const decagonChatPath = getDecagonChatPath(pathname, search);

  const isMobile = useSelector(selectIsMobile);
  const locale = useSelector(selectLocale);
  const venue = useSelector(selectActiveVenue);
  const modals = useSelector(selectModals);
  const { adminUserId } = useSelector(selectUserInfo);

  const { currentItem, setCurrentItem } = useContext(ItemFormContext);
  const { closeFlyout } = useContext(FlyoutContext);
  const [showAvailabilityRecurrence, setShowAvailabilityRecurrence] = useState(false);
  const [addItemFlyoutId, setAddItemFlyoutId] = useState(undefined);

  const formRef = useRef(null);
  const isAvailabilityModalOpenRef = useRef(false);

  const isAdminUser = !!adminUserId;
  isAvailabilityModalOpenRef.current = !!modals[ITEM_AVAILABILITY_MODAL_NAME]?.isOpen;
  let initialAvailability = defaultAvailability || DEFAULT_AVAILABILITY;

  useEffect(() => {
    const flyoutId = generateFlyoutId(ADD_ITEM_FLYOUT_ID_PREFIX, currentItem?.name || undefined);
    setAddItemFlyoutId(flyoutId);
  }, [currentItem]);

  const handleClose = () => {
    closeFlyout();
    setCurrentItem({});
  };

  const toggleShowAvailabilityRecurrence = () => {
    setShowAvailabilityRecurrence(!showAvailabilityRecurrence);
  };

  const { countryCodeShort } = venue;
  const isTaxExcludedMenuPrice = getIsTaxExcludedMenuPrice(countryCodeShort);
  const menuPriceDescription = isTaxExcludedMenuPrice
    ? messages.taxExcludedMenuPriceDescription
    : messages.taxIncludedMenuPriceDescription;

  const getDefaultValues = () => {
    const { activeTagIds = [], description = '', category = '' } = currentItem;
    let currentCategoryTag = '';
    if (category) {
      currentCategoryTag = categoryTags.find(tag => tag.name === category) || '';
    } else if (activeTagIds.includes(foodAndBeverageL1Tag?.id)) {
      currentCategoryTag = foodAndBeverageL1Tag;
    }

    return {
      itemName: '',
      itemDescription: description,
      category: currentCategoryTag,
      linkToMenu: '',
      availabilityRecurrence: initialAvailability,
    };
  };

  let { itemNamePlaceholder } = messages;
  let itemNamePlaceholderValues = {};
  let isDuplicate = false;

  if (!_isEmpty(currentItem)) {
    const { name, availabilityRecurrence } = currentItem;
    itemNamePlaceholder = messages.duplicateItemNamePlaceholder;
    itemNamePlaceholderValues = { duplicateItemName: name };
    initialAvailability = availabilityRecurrence;
    isDuplicate = true;
  }

  function overrideFlyoutOutsideClick(event) {
    if (isAvailabilityModalOpenRef.current) return true;
    if (event?.target && !document.body.contains(event.target)) return true;
    return false;
  }

  return (
    <Flyout
      id={addItemFlyoutId}
      className={block()}
      onClose={handleClose}
      showBackButton={isMobile}
      overrideOutsideClick={overrideFlyoutOutsideClick}>
      <FlyoutTitle>
        <Message message={messages.addItem} />
      </FlyoutTitle>
      <Form isLoading={isLoading} id={FORM_NAME} defaultValues={getDefaultValues()}>
        {function FormRender({ formState, handleSubmit, reset, watch, setValue }) {
          const { errors, isDirty, isSubmitted, isSubmitting, submitCount } = formState;
          const [watchAvailabilityRecurrence, itemName = '', itemDescription = '', menuPrice, linkToMenu, category] =
            watch(['availabilityRecurrence', 'itemName', 'itemDescription', 'menuPrice', 'linkToMenu', 'category']);

          const resetForm = () => {
            if (formRef.current) {
              formRef.current.scrollIntoView();
            }

            const defaultValues = getDefaultValues();
            reset(defaultValues);
          };

          useEffect(() => {
            resetForm();
          }, [currentItem]);

          async function onSubmitItemAvailability(selectedAvailability) {
            if (selectedAvailability) {
              try {
                await createItem(
                  venue,
                  { itemName, itemDescription, category, duration: DEFAULT_DURATION },
                  selectedAvailability
                );

                if (!isAdminUser) {
                  try {
                    await submitMenuPricing(venue, { itemName, menuPrice, linkToMenu });
                  } catch (error) {
                    reportError('Could not submit menu pricing for Item', error);
                    handleClose();
                    dispatch(closeModal());
                    dispatch(
                      openModal(ITEM_SUBMIT_ERROR_MODAL_NAME, {
                        currencyCode,
                        itemName,
                        linkToMenu,
                        menuPrice,
                      })
                    );
                    return;
                  }
                }

                onSuccess?.();
                handleClose();
                dispatch(closeModal());
                dispatch(openModal(ITEM_IN_REVIEW_MODAL_NAME, { itemName }));
              } catch (error) {
                handleClose();
                dispatch(closeModal());
                dispatch(
                  createErrorSnackbar(
                    messages.errorAddItem,
                    { strong: chunks => <strong>{chunks}</strong> },
                    { isLowStyle: true },
                    errorSnackbarDescriptionWithValues(decagonChatPath)
                  )
                );
              }
            }
          }

          function openFirstItemAvailabilityModal() {
            return new Promise(resolve => {
              let hasSubmitted = false;

              dispatch(
                openModal(ITEM_AVAILABILITY_MODAL_NAME, {
                  initialAvailability,
                  onSubmit: async ({ availabilityRecurrence }) => {
                    hasSubmitted = true;
                    segment.event('Click save and add item', { venueId: venue.venueId, isDuplicate });
                    await onSubmitItemAvailability(availabilityRecurrence);
                    resolve();
                  },
                  onClose: () => {
                    if (!hasSubmitted) {
                      segment.event('Click Availability Cancel', { venueId: venue.venueId, isDuplicate });
                      resolve();
                    }
                  },
                  id: MODAL_ID,
                })
              );
            });
          }

          async function openSubsequentItemAvailabilityModal() {
            const itemAvailabilityPromise = new Promise(resolve => {
              function onSubmitSubsequentItemAvailability({ availabilityRecurrence: itemAvailabilityRecurrence }) {
                resolve({ updatedAvailability: itemAvailabilityRecurrence });
              }

              dispatch(
                openModal(ITEM_AVAILABILITY_MODAL_NAME, {
                  isEditForm: true,
                  initialAvailability: watchAvailabilityRecurrence || initialAvailability,
                  onSubmit: onSubmitSubsequentItemAvailability,
                  id: MODAL_ID,
                })
              );
            });

            const { updatedAvailability } = await itemAvailabilityPromise;

            if (updatedAvailability) {
              setValue(
                'availabilityRecurrence',
                updateAvailabilityRecurrence(watchAvailabilityRecurrence, updatedAvailability),
                { shouldDirty: true }
              );
            }

            dispatch(closeModal(ITEM_AVAILABILITY_MODAL_NAME));
          }

          async function onSubmitFirstItem() {
            segment.event('Click Add Item Availability', { venueId: venue.venueId, isDuplicate });
            await openFirstItemAvailabilityModal();
          }

          async function onSubmitSubsequentItem() {
            return onSubmitItemAvailability(watchAvailabilityRecurrence);
          }

          useEffect(() => {
            if (isSubmitted && !_isEmpty(errors)) {
              if (formRef.current) {
                formRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }
          }, [submitCount]);

          const submitButton = () =>
            defaultAvailability ? (
              <Message
                form={FORM_NAME}
                as={Button}
                className={element('submit-btn')}
                disabled={!isDirty || isSubmitting}
                onClick={handleSubmit(onSubmitSubsequentItem)}
                message={messages.submitButton}
                styled="primary"
                type={Button.TYPES.SUBMIT}
              />
            ) : (
              <Message
                form={FORM_NAME}
                as={Button}
                className={element('submit-btn')}
                disabled={!isDirty || isSubmitting}
                onClick={handleSubmit(onSubmitFirstItem)}
                message={messages.addItemAvailabilityButton}
                styled="primary"
                type={Button.TYPES.SUBMIT}
              />
            );

          return (
            <>
              <FlyoutContent>
                <div className={element('input-wrapper')} ref={formRef}>
                  <When predicate={!_isEmpty(errors)}>
                    <div className={element('error-banner')}>
                      <Isvg className={element('error-icon')} src={ICONS.error} />
                      <Message as="div" message={messages.submitError} />
                    </div>
                  </When>
                  <Input
                    name="itemName"
                    disabled={isSubmitting}
                    label={messages.itemNameLabel}
                    maxLength={NAME_MAX_INPUT_LENGTH}
                    placeholder={itemNamePlaceholder}
                    placeholderValues={itemNamePlaceholderValues}
                    type="text"
                    validate={{
                      message: messages.itemNameMustBeUnique,
                      value: value => !existingItemNames.includes(value.toLowerCase()),
                    }}
                    required
                    showRequiredLabel
                  />
                  <When predicate={NAME_MAX_INPUT_LENGTH - itemName.length <= 10}>
                    <div
                      className={element('characters-remaining', {
                        error: NAME_MAX_INPUT_LENGTH === itemName.length,
                      })}>
                      <CharactersRemaining length={NAME_MAX_INPUT_LENGTH} value={itemName} showMaxLength />
                    </div>
                  </When>
                  <Textarea
                    name="itemDescription"
                    disabled={isSubmitting}
                    heightInLines={DESCRIPTION_INPUT_HEIGHT_IN_LINES}
                    label={messages.itemDescriptionLabel}
                    maxLength={DESCRIPTION_MAX_INPUT_LENGTH}
                    placeholder={messages.itemDescriptionPlaceholder}
                    type="text"
                    required
                    showRequiredLabel
                  />
                  <When predicate={DESCRIPTION_MAX_INPUT_LENGTH - itemDescription.length <= 10}>
                    <div
                      className={element('characters-remaining', {
                        error: DESCRIPTION_MAX_INPUT_LENGTH === itemDescription.length,
                      })}>
                      <CharactersRemaining
                        length={DESCRIPTION_MAX_INPUT_LENGTH}
                        value={itemDescription}
                        showMaxLength
                      />
                    </div>
                  </When>
                  <Dropdown
                    className={element('category-dropdown')}
                    name="category"
                    disabled={isSubmitting}
                    label={messages.selectCategory}
                    required
                    showRequiredLabel>
                    {({ value: selectedOption, onChange }) => {
                      return (
                        <>
                          <DropdownToggle className={element('category-dropdown-toggle', { selected: selectedOption })}>
                            <When predicate={!selectedOption}>
                              <Message as="div" message={messages.selectCategory} />
                            </When>
                            <When predicate={!!selectedOption}>
                              {selectedOption.id === foodAndBeverageL1Tag?.id ? (
                                <Message as="div" message={messages.otherCategory} />
                              ) : (
                                selectedOption.name || selectedOption
                              )}
                            </When>
                          </DropdownToggle>
                          <DropdownMenu>
                            {categoryTags.map(categoryTag => {
                              return (
                                <DropdownItem
                                  key={categoryTag.id}
                                  isSelected={selectedOption?.id === categoryTag.id}
                                  onClick={() => onChange(categoryTag)}>
                                  <div>{categoryTag.name}</div>
                                </DropdownItem>
                              );
                            })}
                            <DropdownItem
                              key={foodAndBeverageL1Tag?.id}
                              isSelected={selectedOption?.id === foodAndBeverageL1Tag?.id}
                              onClick={() => onChange(foodAndBeverageL1Tag)}>
                              <Message as="div" message={messages.otherCategory} />
                            </DropdownItem>
                          </DropdownMenu>
                        </>
                      );
                    }}
                  </Dropdown>

                  <When predicate={!isAdminUser}>
                    <div className={element('info-notice')}>
                      <div className={element('info-content')}>
                        <Isvg className="info-icon" src={ICONS.infoBrand} />
                        <Message className="info-message" message={messages.priceVerification} />
                      </div>
                    </div>
                    <CurrencyInput
                      className={element('currency-input')}
                      name="menuPrice"
                      currencyCode={currencyCode}
                      locale={locale}
                      disabled={isSubmitting}
                      label={messages.menuPriceLabel}
                      required
                      showRequiredLabel
                    />

                    <Message as="div" className={element('input-description')} message={menuPriceDescription} />
                    <Input
                      className={element('link-to-menu-input')}
                      name="linkToMenu"
                      disabled={isSubmitting}
                      label={messages.linkToMenuLabel}
                      placeholder={messages.linkToMenuPlaceholder}
                      pattern={{
                        value: URL_SIMPLE_REGEX,
                        message: messages.invalidUrl,
                      }}
                      type="text"
                      required
                      showRequiredLabel
                    />
                    <Message
                      as="div"
                      className={element('input-description')}
                      message={messages.linkToMenuDescription}
                    />
                  </When>

                  <When predicate={!!defaultAvailability}>
                    <div className={element('item-availability-dropdown')}>
                      <button
                        type="button"
                        className={element('item-availability-expand-btn')}
                        onClick={toggleShowAvailabilityRecurrence}
                        aria-expanded={showAvailabilityRecurrence}
                        aria-label={
                          showAvailabilityRecurrence
                            ? 'Collapse availability recurrence'
                            : 'Expand availability recurrence'
                        }>
                        <div
                          className={element('item-availability-title-wrapper', {
                            isExpanded: showAvailabilityRecurrence,
                          })}>
                          <Message
                            as="div"
                            className={element('item-availability-label')}
                            message={messages.itemAvailabilityLabel}
                          />
                          <Message as="div" message={messages.optionalLabel} />
                          <SvgArrow direction={showAvailabilityRecurrence ? 'up' : 'down'} />
                        </div>
                      </button>
                      <When predicate={showAvailabilityRecurrence}>
                        <div className={element('item-availability-description-wrapper')}>
                          <Message
                            as="div"
                            className={element('item-availability-description')}
                            message={messages.subsequentItemAvailabilityDescription}
                            values={{ br: () => <br /> }}
                          />
                          <Button
                            className={element('icon-button')}
                            styled="tertiary"
                            onClick={openSubsequentItemAvailabilityModal}>
                            <Isvg src={ICONS.pencilFilled} />
                            <Message className={element('button-text')} as="div" message={messages.editButton} />
                          </Button>
                        </div>
                        {watchAvailabilityRecurrence?.map(day => {
                          const { available, times, weekday } = day;
                          const sortedTimes = times.sort((a, b) => {
                            const startTimeA = parse(a.startTime, 'HH:mm', new Date());
                            const startTimeB = parse(b.startTime, 'HH:mm', new Date());
                            return startTimeA - startTimeB;
                          });

                          return (
                            <div key={weekday} className={element('item-availability-day')}>
                              <div>{weekday}</div>
                              <When predicate={!available}>
                                <Message message={messages.availabilityUnavailable} />
                              </When>
                              <When predicate={available}>
                                <div className={element('item-availability-times')}>
                                  {sortedTimes.map(({ startTime, endTime, idx }) => {
                                    const parsedStartTime = parse(startTime, 'HH:mm', new Date());
                                    const parsedEndTime = parse(endTime, 'HH:mm', new Date());

                                    const formattedStartTime = format12HourTimeLowercase(parsedStartTime, locale);
                                    const formattedEndTime = format12HourTimeLowercase(parsedEndTime, locale);

                                    return (
                                      <Message
                                        key={`${weekday}-${idx}`}
                                        message={messages.availabilityTimeWindow}
                                        values={{ startTime: formattedStartTime, endTime: formattedEndTime }}
                                      />
                                    );
                                  })}
                                </div>
                              </When>
                            </div>
                          );
                        })}
                      </When>
                    </div>
                  </When>
                </div>
              </FlyoutContent>
              <FlyoutFooter>
                <ButtonGroup className={element('button-group')} divider={false} flush>
                  <Message
                    as={Button}
                    className={element('cancel-btn')}
                    disabled={isSubmitting}
                    message={messages.cancelButton}
                    onClick={handleClose}
                    styled="tertiary"
                    type={Button.TYPES.BUTTON}
                  />

                  {submitButton()}
                </ButtonGroup>
              </FlyoutFooter>
            </>
          );
        }}
      </Form>
    </Flyout>
  );
};

AddItemForm.propTypes = {
  isLoading: PropTypes.bool,
  currencyCode: PropTypes.string,
  categoryTags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  existingItemNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  foodAndBeverageL1Tag: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
  defaultAvailability: PropTypes.arrayOf(PropTypes.shape({})),
  onSuccess: PropTypes.func,
};

export default AddItemForm;
